import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/system/Box';
import {
  usePopupState,
  bindHover,
  bindPopover
} from 'material-ui-popup-state/hooks';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { useUpdateNotificationsDateMutation } from 'store/api';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import { Fragment, useEffect, useRef, useState } from 'react';
import Divider from '@mui/material/Divider';
import { NotificationCount } from './styled';
import { notificationOptions } from './authUserMenuData';

const NotificationMenu = ({
  userId,
  hasUnacknowledged,
  notifications,
  userType,
  resetNotificationAcknowledgeState
}: {
  userId: string;
  hasUnacknowledged: boolean;
  notifications: Notifications;
  userType: number;
  resetNotificationAcknowledgeState: () => void;
}) => {
  const [menuBottom, setMenuBottom] = useState<number>(-1);
  const containerRef = useRef<HTMLInputElement>(null);
  const notificationMenuRef = useRef<HTMLInputElement>(null);
  const popupStateRef = useRef<boolean>(false); // To save old value of popup state isOpen

  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'notification'
  });

  const [updateNotificationsDate] = useUpdateNotificationsDateMutation();
  const updateNotificationsStatus = () => {
    if (notifications && hasUnacknowledged) updateNotificationsDate(userId);
  };

  const calcAndSetMenuBottom = (isForceCalc?: boolean) => {
    if (!notificationMenuRef.current && !isForceCalc) return;
    const { bottom } = containerRef.current.getBoundingClientRect();
    if (bottom !== menuBottom) setMenuBottom(bottom);
  };

  useEffect(() => {
    if (!containerRef.current) return;
    setMenuBottom(containerRef.current.getBoundingClientRect().bottom);
  }, []);

  useEffect(() => {
    if (popupStateRef.current && !popupState.isOpen)
      resetNotificationAcknowledgeState();

    if (popupStateRef.current !== popupState.isOpen)
      popupStateRef.current = popupState.isOpen;
  }, [popupState.isOpen]);

  useEffect(() => {
    document.addEventListener('scroll', () => calcAndSetMenuBottom());
    return () => {
      document.removeEventListener('scroll', () => calcAndSetMenuBottom());
    };
  }, []);

  const onMenuFocus = () => {
    calcAndSetMenuBottom(true);
    updateNotificationsStatus();
  };

  const togglePopupState = () => {
    if (popupState.isOpen) popupState.close();
    else popupState.open();
  };

  return (
    <>
      <Box
        ref={containerRef}
        sx={{ alignItems: 'center', textAlign: 'center' }}>
        <IconButton
          onPointerEnter={onMenuFocus}
          onClick={togglePopupState}
          {...bindHover(popupState)}
          sx={{
            border: '1px solid white',
            height: '24px',
            width: '24px',
            backgroundColor: 'white',
            marginLeft: {
              xs: '10px',
              md: '20px'
            },
            marginTop: {
              xs: '-2px',
              md: 'auto'
            },
            '&:hover': {
              backgroundColor: 'white'
            }
          }}>
          <Badge
            color="error"
            variant="dot"
            invisible={!notifications || !hasUnacknowledged}>
            <NotificationsNoneIcon
              sx={{
                fill: '#299694',
                height: '17px',
                width: '17px'
              }}
            />
          </Badge>
        </IconButton>
      </Box>

      <HoverPopover
        ref={notificationMenuRef}
        disableScrollLock
        PaperProps={{
          sx: {
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            marginTop: `${menuBottom - 5}px !important`,
            marginLeft: '9px',
            overflow: 'visible',
            top: '0 !important'
          }
        }}
        {...bindPopover(popupState)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}>
        <Box
          sx={{
            opacity: '0',
            height: '25px'
          }}
        />
        <Box
          sx={{
            backgroundColor: 'white',
            elevation: 0,
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            padding: '10px 0',
            width: {
              xs: '250px',
              sm: '292px'
            },
            '& .MuiMenuItem-root': {
              borderLeft: '7px solid white'
            },
            '& .MuiMenuItem-root:hover': {
              borderLeft: '7px solid #09b3b0',
              backgroundColor: 'rgba(186,232,231,.3)'
            },
            '& .MuiMenuItem-root:focus': {
              borderLeft: '7px solid #09b3b0',
              backgroundColor: 'rgba(186,232,231,.3)'
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'relative',
              marginLeft: 'auto',
              marginRight: '3px',
              marginTop: '-6px',
              width: 28,
              height: 16,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0
            }
          }}>
          {notificationOptions
            .filter((x) => x.userTypes.includes(userType))
            .map((link: UserMenuOptions) => (
              <Fragment key={link.name}>
                <MenuItem
                  sx={{
                    padding: '0'
                  }}>
                  <Link
                    key={link.name}
                    href={link.href}
                    underline="none"
                    sx={{
                      display: 'flex',
                      width: '100%',
                      color: 'black',
                      fontSize: '18px',
                      lineHeight: 1,
                      padding: '12px 20px'
                    }}>
                    {link.itemKey &&
                      notifications &&
                      notifications[link.itemKey] > 0 && (
                        <NotificationCount
                          unacknowledged={
                            link.unAcknowledgedKey &&
                            notifications[link.unAcknowledgedKey] &&
                            hasUnacknowledged
                              ? 'true'
                              : undefined
                          }>
                          {notifications[link.itemKey] > 99
                            ? '99+'
                            : notifications[link.itemKey]}
                        </NotificationCount>
                      )}
                    {link.name}
                  </Link>
                </MenuItem>
                {link.divider && (
                  <Divider sx={{ margin: '8px 0 !important' }} />
                )}
              </Fragment>
            ))}
        </Box>
      </HoverPopover>
    </>
  );
};

export default NotificationMenu;
